/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  downloadFile,
  formatFecha,
  Sidebar,
  tituloBase,
  VisorArchivo,
  TablaFiltrable,
} from "../../../../shared";

const SEARCH_FIELDS_NO_VIGENTES = ["codigo", "nombre"];

export default function Documentos() {
  const [loaderDocs, setLoaderDocs] = useState(true);
  const [loaderCarpetas, setLoaderCarpetas] = useState(true);
  const [vigentes, setVigentes] = useState([]);
  const [noVigentes, setNoVigentes] = useState([]);
  const [carpetas, setCarpetas] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const [descargandoId, setDescargandoId] = useState(null);
  const [descargandoExcel, setDescargandoExcel] = useState(false);
  const [firstByCarpeta, setFirstByCarpeta] = useState({});
  const [firstNoVigentes, setFirstNoVigentes] = useState(0);
  const [activeIndex, setActiveIndex] = useState([]);
  // Documento sobre el que se abrió el menú de acciones, y el que se está
  // viendo en la vista previa.
  const [documentoMenu, setDocumentoMenu] = useState(null);
  const [documentoVisor, setDocumentoVisor] = useState(null);
  const menuRef = useRef(null);

  const loader = loaderDocs || loaderCarpetas;

  const accordionSinTransicion = {
    transition: { timeout: 0 },
  };

  const toast = useRef(null);
  const navigate = useNavigate();

  function crearDocumento(camposFormulario) {
    setFormLoader(true);
    accessAPI(
      "POST",
      "admin/documento",
      camposFormulario,
      (respuesta) => {
        navigate(`/documento/${respuesta.id}`);
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear documento",
          sticky: true,
        });
      },
    );
  }

  // Archivo de la versión vigente para la vista previa: el PDF generado si
  // existe, si no el original (mismo criterio que la descarga).
  const archivoVigente = (documento) => {
    const version = documento?.documentoVersions?.[0];
    return version?.archivoPDF || version?.archivo || null;
  };

  const opcionesMenu = documentoMenu
    ? [
        {
          label: "Descargar",
          icon: "pi pi-download",
          command: () => descargarVigente(documentoMenu),
        },
        {
          label: "Vista previa",
          icon: "pi pi-eye",
          command: () => setDocumentoVisor(documentoMenu),
        },
      ]
    : [];

  // Botón de tres puntos de cada fila; el spinner aparece mientras descarga.
  const botonAcciones = (rowData) => (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5em",
      }}
    >
      {descargandoId === rowData.id && <i className="pi pi-spin pi-spinner" />}
      <Boton
        texto
        icono
        chico
        negro
        callback={(e) => {
          e.stopPropagation();
          setDocumentoMenu(rowData);
          menuRef.current.toggle(e);
        }}
      >
        <i className="pi pi-ellipsis-v" />
      </Boton>
    </div>
  );

  function descargarVigente(documento) {
    const versionLiberada = documento.documentoVersions[0];
    if (!versionLiberada) return;
    const archivoIdDescarga =
      versionLiberada.archivoPDFId || versionLiberada.archivoId;
    // Nombre de descarga: {codigo} - {nombre} - v.{version}, conservando la
    // extensión del archivo original
    const nombreOriginal = versionLiberada.archivoPDFId
      ? versionLiberada.archivoPDF?.nombredescarga
      : versionLiberada.archivo.nombredescarga;
    const ext = nombreOriginal?.includes(".")
      ? `.${nombreOriginal.split(".").pop()}`
      : "";
    const nombreDescarga = `${documento.codigo} - ${documento.nombre} - v.${versionLiberada.version}${ext}`;
    setDescargandoId(documento.id);
    downloadFile(
      `admin/archivo/${archivoIdDescarga}`,
      nombreDescarga,
      () => setDescargandoId(null),
      (error) => {
        setDescargandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar archivo",
          sticky: true,
        });
      },
    );
  }

  const crearFormConfig = {
    secciones: [
      {
        nombre: "Datos del documento",
        campos: [
          {
            nombre: "codigo",
            tipoitemformularioid: 1,
            etiqueta: "Codigo",
            requerido: true,
          },
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true,
          },
          {
            nombre: "resumen",
            tipoitemformularioid: 11,
            etiqueta: "Resumen",
          },
          {
            nombre: "documentoCarpetaId",
            tipoitemformularioid: 5,
            etiqueta: "Carpeta",
            opciones: "admin/documentocarpeta",
            sinOpcionInicial: true,
            opcionVacia: true,
          },
          {
            nombre: "listaDistribucion",
            tipoitemformularioid: 11,
            etiqueta: "Lista de distribución (correos separados por coma)",
          },
          {
            nombre: "externo",
            tipoitemformularioid: 6,
            etiqueta: "Documento externo",
          },
        ],
      },
    ],
    botones: [
      { etiqueta: "Crear documento", callback: crearDocumento, validar: true },
    ],
  };

  useEffect(() => {
    document.title = "Documentos";
    accessAPI(
      "GET",
      "admin/documento",
      null,
      (respuesta) => {
        setVigentes(respuesta.filter((d) => d.vigente));
        setNoVigentes(respuesta.filter((d) => !d.vigente));
        setLoaderDocs(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar documentos",
          sticky: true,
        });
        setLoaderDocs(false);
      },
    );
    accessAPI(
      "GET",
      "admin/documentocarpeta/lista",
      null,
      (respuesta) => {
        setCarpetas(respuesta);
        setLoaderCarpetas(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar carpetas",
          sticky: true,
        });
        setLoaderCarpetas(false);
      },
    );
    return () => (document.title = tituloBase());
  }, []);

  const grupos = useMemo(() => {
    const result = carpetas.map((c) => ({
      id: c.id,
      nombre: c.nombre,
      documentos: vigentes.filter((d) => d.documentoCarpetaId === c.id),
    }));
    const sinCarpeta = vigentes.filter((d) => !d.documentoCarpetaId);
    if (sinCarpeta.length > 0) {
      result.push({ id: null, nombre: "Sin carpeta", documentos: sinCarpeta });
    }
    return result;
  }, [carpetas, vigentes]);

  // Sin tildes ni mayúsculas: es un filtro en memoria, no hay que tocar la
  // base para esto (a diferencia del buscador general del header, pospuesto).
  function normalizar(texto) {
    return (texto || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  }

  const filtroNormalizado = normalizar(globalFilter);

  // Con texto en el buscador, se filtran los documentos de cada carpeta y se
  // descartan las carpetas sin ningún match — así se puede buscar sin
  // necesidad de tener las carpetas ya desplegadas a mano.
  const gruposFiltrados = useMemo(() => {
    if (!filtroNormalizado) return grupos;
    return grupos
      .map((grupo) => ({
        ...grupo,
        documentos: grupo.documentos.filter(
          (d) =>
            normalizar(d.codigo).includes(filtroNormalizado) ||
            normalizar(d.nombre).includes(filtroNormalizado),
        ),
      }))
      .filter((grupo) => grupo.documentos.length > 0);
  }, [grupos, filtroNormalizado]);

  // Mientras hay una búsqueda activa, las carpetas con match quedan abiertas
  // solas (todas: ya se descartaron las que no tienen ninguno). Al borrar el
  // buscador se vuelve a lo que el usuario tenía abierto a mano.
  const activeIndexEfectivo = filtroNormalizado
    ? gruposFiltrados.map((_, i) => i)
    : activeIndex;

  function onRowClick(e) {
    if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
      window.open(`/documento/${e.data.id}`, "_blank");
    } else {
      navigate(`/documento/${e.data.id}`);
    }
  }

  const tableProps = {
    stripedRows: true,
    paginator: true,
    rows: 10,
    onRowClick,
    css: { "& .p-datatable-tbody > tr": { cursor: "pointer" } },
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo documento</Titulo>
        <Formulario config={crearFormConfig} loader={formLoader} />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="16rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            botones={
              <>
                <Boton
                  transparente
                  callback={() => {
                    setDescargandoExcel(true);
                    downloadFile(
                      "admin/documento/excel",
                      `Documentos vigentes - ${formatFecha(new Date())}.xlsx`,
                      () => setDescargandoExcel(false),
                      (error) => {
                        setDescargandoExcel(false);
                        toast.current?.show({
                          severity: "error",
                          summary: "Error",
                          detail: error[0]?.msg || "Error al descargar listado",
                          sticky: true,
                        });
                      },
                    );
                  }}
                  loader={descargandoExcel}
                >
                  Descargar listado
                </Boton>
                <Boton callback={() => setSideBar(true)}>Nuevo documento</Boton>
              </>
            }
          >
            Documentos
          </Titulo>

          <Card>
            <div
              css={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "0.5em",
              }}
            >
              <span
                className="p-input-icon-left"
                css={{
                  width: "33%",
                  "& .pi-search": { left: "0.75rem" },
                  "@media (max-width: 768px)": { width: "100%" },
                }}
              >
                <i className="pi pi-search" />
                <InputText
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Buscar..."
                  css={{ width: "100%", paddingLeft: "2.5rem" }}
                />
              </span>
            </div>

            {grupos.length === 0 && (
              <div css={{ textAlign: "center", color: "#6c757d" }}>
                No hay carpetas ni documentos vigentes
              </div>
            )}

            {grupos.length > 0 && gruposFiltrados.length === 0 && (
              <div css={{ textAlign: "center", color: "#6c757d" }}>
                Sin resultados para "{globalFilter}"
              </div>
            )}

            {gruposFiltrados.length > 0 && (
              <Accordion
                multiple
                activeIndex={activeIndexEfectivo}
                onTabChange={(e) => setActiveIndex(e.index)}
                css={{
                  "& .p-accordion-header-link": { color: "black !important" },
                }}
              >
                {gruposFiltrados.map((grupo) => {
                  const claveCarpeta = grupo.id ?? "sin";
                  return (
                    <AccordionTab
                      key={claveCarpeta}
                      header={`${grupo.nombre} (${grupo.documentos.length})`}
                      pt={accordionSinTransicion}
                    >
                      <TablaFiltrable conFiltros={false}
                        key={`vigentes-${claveCarpeta}-${descargandoId}`}
                        first={firstByCarpeta[claveCarpeta] || 0}
                        onPage={(e) =>
                          setFirstByCarpeta((prev) => ({
                            ...prev,
                            [claveCarpeta]: e.first,
                          }))
                        }
                        value={grupo.documentos}
                        emptyMessage="No hay documentos en esta carpeta"
                        {...tableProps}
                      >
                        <Column
                          header="Documento"
                          body={(rowData) => (
                            <span
                              css={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5em",
                              }}
                            >
                              {rowData.codigo} - {rowData.nombre}
                              {rowData.externo && (
                                <Tag
                                  value="Externo"
                                  css={{ backgroundColor: "var(--principal)", color: "#ffffff" }}
                                />
                              )}
                            </span>
                          )}
                        />
                        <Column
                          header="Colaboradores"
                          body={(rowData) =>
                            rowData.disponibleAuditor ? (
                              <i
                                className="pi pi-check"
                                css={{ color: "var(--verde)" }}
                              />
                            ) : null
                          }
                          style={{ width: "8rem", textAlign: "center" }}
                        />
                        <Column
                          body={(rowData) => {
                            if (rowData.externo) {
                              return (
                                <Boton
                                  icono
                                  negro
                                  callback={(e) => {
                                    e.stopPropagation();
                                    if (rowData.linkExterno)
                                      window.open(
                                        rowData.linkExterno,
                                        "_blank",
                                      );
                                  }}
                                  disabled={!rowData.linkExterno}
                                >
                                  <i className="pi pi-eye" />
                                </Boton>
                              );
                            }
                            if (!rowData.documentoVersions?.length) return null;
                            return botonAcciones(rowData);
                          }}
                          style={{ width: "5rem" }}
                        />
                      </TablaFiltrable>
                    </AccordionTab>
                  );
                })}
              </Accordion>
            )}

            {noVigentes.length > 0 && (
              <Accordion
                css={{
                  marginTop: "1.5em",
                  "& .p-accordion-header-link": { color: "black !important" },
                }}
              >
                <AccordionTab header="No vigentes" pt={accordionSinTransicion}>
                  <TablaFiltrable
                    key={`noVigentes-${descargandoId}`}
                    first={firstNoVigentes}
                    onPage={(e) => setFirstNoVigentes(e.first)}
                    value={noVigentes}
                    emptyMessage="No hay documentos no vigentes"
                    searchFields={SEARCH_FIELDS_NO_VIGENTES}
                    anchoBusqueda="33%"
                    nombreArchivo="documentos_no_vigentes"
                    {...tableProps}
                  >
                    <Column
                      header="Documento"
                      body={(rowData) => (
                        <span
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em",
                          }}
                        >
                          {rowData.codigo} - {rowData.nombre}
                          {rowData.externo && (
                            <Tag
                              value="Externo"
                              css={{ backgroundColor: "var(--principal)", color: "#ffffff" }}
                            />
                          )}
                        </span>
                      )}
                    />
                    <Column
                      body={(rowData) => {
                        if (!rowData.documentoVersions?.length) return null;
                        return botonAcciones(rowData);
                      }}
                      style={{ width: "5rem" }}
                    />
                  </TablaFiltrable>
                </AccordionTab>
              </Accordion>
            )}
          </Card>
        </div>
      )}

      <Menu model={opcionesMenu} popup ref={menuRef} />
      <VisorArchivo
        visible={!!documentoVisor}
        onHide={() => setDocumentoVisor(null)}
        nombre={archivoVigente(documentoVisor)?.nombredescarga}
        hash={archivoVigente(documentoVisor)?.hash}
        titulo={
          documentoVisor
            ? `${documentoVisor.codigo} - ${documentoVisor.nombre} - v.${documentoVisor.documentoVersions[0].version}`
            : ""
        }
      />
    </>
  );
}
